// Radha — the country viewing lens.
//
// One selected country, shared by every jurisdiction-aware surface. It is a
// LENS, not a data change: the tenant of record, its currency and its book
// stay exactly as they are — what switches is which country's rules,
// statutes, calendars and labels the panels compute and display.
//
// Module-level state + subscription (the same pattern as i18n): panels call
// useViewCountry() and re-render when the header selector changes.
import { useSyncExternalStore } from "react";

const KEY = "radha.viewCountry";

// No flag emoji: Windows ships no colour flag font, so Chrome falls back to
// the bare regional-indicator letters and a flag + "India" renders as
// "IN India" - which reads as a mysterious two-letter prefix. Plain names only.
export interface CountryDef { code: string; label: string; currency: string; locale: string }
export const COUNTRIES: CountryDef[] = [
  { code: "IN", label: "India",          currency: "INR", locale: "en-IN" },
  { code: "US", label: "United States",  currency: "USD", locale: "en-US" },
  { code: "GB", label: "United Kingdom", currency: "GBP", locale: "en-GB" },
  { code: "DE", label: "Germany",        currency: "EUR", locale: "de-DE" },
  { code: "FR", label: "France",         currency: "EUR", locale: "fr-FR" },
  { code: "NL", label: "Netherlands",    currency: "EUR", locale: "nl-NL" },
  { code: "ES", label: "Spain",          currency: "EUR", locale: "es-ES" },
  { code: "IT", label: "Italy",          currency: "EUR", locale: "it-IT" },
  { code: "AE", label: "UAE",            currency: "AED", locale: "en-AE" },
  { code: "SA", label: "Saudi Arabia",   currency: "SAR", locale: "en-SA" },
];
export const countryDef = (code: string) => COUNTRIES.find((c) => c.code === code);

let override: string | null = null;
try {
  const saved = localStorage.getItem(KEY);
  if (saved && COUNTRIES.some((c) => c.code === saved)) override = saved;
} catch { /* private mode */ }

const listeners = new Set<() => void>();

/** null = follow the tenant's own country (the default). */
export function setViewCountry(code: string | null): void {
  override = code;
  try {
    if (code) localStorage.setItem(KEY, code);
    else localStorage.removeItem(KEY);
  } catch { /* private mode */ }
  listeners.forEach((l) => l());
}

export function getViewOverride(): string | null {
  return override;
}

// ---- reporting currency -----------------------------------------------
// "auto" follows the country lens (Germany -> EUR); an explicit code pins
// every figure to that currency regardless of which country is in view,
// which is how a group CFO reads subsidiaries side by side.
const CUR_KEY = "radha.reportingCurrency";
let currencyOverride: string | null = null;
try {
  const saved = localStorage.getItem(CUR_KEY);
  if (saved) currencyOverride = saved;
} catch { /* private mode */ }

export function setReportingCurrency(code: string | null): void {
  currencyOverride = code;
  try {
    if (code) localStorage.setItem(CUR_KEY, code);
    else localStorage.removeItem(CUR_KEY);
  } catch { /* private mode */ }
  listeners.forEach((l) => l());
}
export function getReportingOverride(): string | null { return currencyOverride; }

function subscribe(l: () => void) {
  listeners.add(l);
  return () => listeners.delete(l);
}

/** The country every jurisdiction-aware panel should compute under. */
export function useViewCountry(tenantCountry: string | null | undefined): string {
  const ov = useSyncExternalStore(subscribe, getViewOverride, getViewOverride);
  return ov ?? tenantCountry ?? "IN";
}

// Compaction convention belongs to the CURRENCY, not to whichever country is
// in view: "$10.12L" (dollars folded into Indian lakhs) is nonsense to every
// reader. When a reporting currency is pinned, money formats in that
// currency's home convention.
const CURRENCY_LOCALE: Record<string, string> = {
  INR: "en-IN", USD: "en-US", EUR: "de-DE", GBP: "en-GB", AED: "en-AE", SAR: "en-SA",
};

/** The currency every figure should render in, and whether it was forced. */
export function useReportingCurrency(tenantCountry: string | null | undefined): {
  currency: string; locale: string; forced: boolean;
} {
  const ov = useSyncExternalStore(subscribe, getReportingOverride, getReportingOverride);
  const view = useViewCountry(tenantCountry);
  const def = countryDef(view) ?? COUNTRIES[0];
  // an explicit choice swaps the unit but keeps the lens country's formatting
  return ov ? { currency: ov, locale: CURRENCY_LOCALE[ov] ?? def.locale, forced: true }
            : { currency: def.currency, locale: def.locale, forced: false };
}

/** Is the lens pointing away from home? Drives the "viewing as" banner. */
export function useIsOverridden(tenantCountry: string | null | undefined): boolean {
  const ov = useSyncExternalStore(subscribe, getViewOverride, getViewOverride);
  return !!ov && ov !== (tenantCountry ?? "IN");
}
